import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BoxExpand,
  Button,
  DeleteModal,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useCreateTaskTemplate } from 'src/hooks/task-templates/create-task-template.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useUpdateTaskTemplate } from 'src/hooks/task-templates/update-task-template.hook';
import { useGetTaskTemplatesByCompany } from 'src/hooks/task-templates/get-task-templates-by-company.hook';
import { useDeleteTaskTemplate } from 'src/hooks/task-templates/delete-task-template.hook';
import PermissionCheck from 'src/components/permission-check';

import {
  TaskTemplateWrapper,
  TaskTemplateTitle,
  IconContainer,
  TaskNamesWrapper,
  TaskName,
} from './project-task-templates.styles';
import { ProjectTaskTemplateModal } from './project-task-templates-modal.component';

const ProjectTaskTemplates = () => {
  const { t } = useTranslation('settings');

  const [activeModal, setActiveModal] = useState<null | 'form' | 'delete'>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const currentCompanyId = useGetCurrentCompanyId();

  const onEditClickHandler = (projectTemplate: any) => {
    setSelectedTemplate(projectTemplate);
    setActiveModal('form');
  };

  const onModalCancelHandler = () => {
    setSelectedTemplate(null);
    setActiveModal(null);
  };

  const onClickDeleteHandler = () => {
    setActiveModal('delete');
  };

  const { taskTemplates } = useGetTaskTemplatesByCompany(currentCompanyId);
  const createTaskTemplate = useCreateTaskTemplate({ onSuccess: onModalCancelHandler });
  const updateTaskTemplate = useUpdateTaskTemplate({ onSuccess: onModalCancelHandler });
  const deleteTaskTemplate = useDeleteTaskTemplate({ onSuccess: onModalCancelHandler });

  const onModalSubmitHandler = (values: any) => {
    if (!selectedTemplate) {
      createTaskTemplate({ variables: { payload: { ...values, companyId: currentCompanyId } } });
    } else {
      updateTaskTemplate({ variables: { payload: { ...values, _id: selectedTemplate._id } } });
    }
  };

  const onDeleteModalSubmitHandler = () => {
    deleteTaskTemplate({ variables: { taskTemplateId: selectedTemplate._id } });
  };

  return (
    <BoxExpand
      title={t('companySettings.labels.projectTaskTemplates')}
      buttons={
        <PermissionCheck
          action={PERMISSION_ACTIONS.create}
          permission={PLATFORM_PERMISSIONS.companySettings}
        >
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal('form')}
          >
            {t('companySettings.buttons.addNewTaskTemplate')}
          </Button>
        </PermissionCheck>
      }
      separator
    >
      {activeModal === 'form' && (
        <ProjectTaskTemplateModal
          onFinish={onModalSubmitHandler}
          onClose={onModalCancelHandler}
          onDelete={onClickDeleteHandler}
          data={selectedTemplate}
        />
      )}

      {activeModal === 'delete' && (
        <DeleteModal
          title={t('companySettings.labels.deleteTaskTemplate')}
          subTitle={t('companySettings.deleteModal.subtitle', {
            fieldName: selectedTemplate?.templateName || '',
          })}
          submitBtnText={t('common:buttons.delete')}
          translate={t}
          isOpened
          onClose={() => setActiveModal('form')}
          onSubmit={onDeleteModalSubmitHandler}
        />
      )}

      {taskTemplates.map((taskTemplate: any) => (
        <TaskTemplateWrapper key={taskTemplate._id}>
          <TaskTemplateTitle>{taskTemplate.templateName}:</TaskTemplateTitle>
          <TaskNamesWrapper>
            {taskTemplate.taskNames.map((taskName: string, index: number) => {
              return <TaskName key={index}>{taskName};</TaskName>;
            })}
            <IconContainer>
              <Icon
                icon={ICONS.editAlt}
                color={PLATFORM_THEME_COLORS.primary.blue}
                size={22}
                rightMargin={false}
                onClick={() => onEditClickHandler(taskTemplate)}
              />
            </IconContainer>
          </TaskNamesWrapper>
        </TaskTemplateWrapper>
      ))}
    </BoxExpand>
  );
};

export default ProjectTaskTemplates;
